import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  type ClinicView,
  clearSavedLoginTarget,
  savedLoginTarget,
} from '../../lib/loginResume';
import { DelayedPageLoader } from '../ui/PageLoader';

export function PublicOnlyRoute() {
  const {
    user,
    isLoading,
    accessibleClinics,
    setActiveClinicId,
    setAllLocationsActive,
  } = useAuth();
  // Not until the session has loaded: a session restored on page load sets its
  // user before its clinics, and a resumed page needs both.
  const userId = isLoading ? undefined : user?.id;
  const location = useLocation();

  // Read redirect target once and memoize it so StrictMode double-renders
  // don't lose the value (sessionStorage reads are side-effect-free).
  const redirectTarget = useMemo((): {
    path: string;
    clinicView?: ClinicView;
  } | null => {
    if (!userId) return null;

    if (location.pathname === '/provider-onboarding') {
      return { path: '/faxes/send' };
    }
    if (location.pathname === '/records-request-onboarding') {
      return { path: '/records-requests/new' };
    }

    const saved = savedLoginTarget(userId);
    if (saved) {
      return saved;
    }

    // Preserve the query string, not just the pathname. Cold deep links (a
    // logged-out user opening a link that carries state in the URL) reach
    // /login through ProtectedRoute's `state.from`, and dropping `search`
    // silently strips that state — e.g. the faxed join-request QR code lands
    // on /staff?inviteEmail=… and the prefill is load-bearing: the accept path
    // requires an exact email match, so a lost or retyped address is an invite
    // the requester can never accept.
    const from = (
      location.state as { from?: { pathname?: string; search?: string } }
    )?.from;
    if (from?.pathname) {
      return { path: `${from.pathname}${from.search ?? ''}` };
    }

    return { path: '/' };
  }, [userId, location.pathname, location.state]);

  // A resumed page opens only under the clinic view it was saved under, never
  // the one another tab left in shared storage; a page whose clinic the user
  // can no longer reach is not resumed at all.
  const clinicView = redirectTarget?.clinicView;
  const viewReachable =
    !clinicView?.clinicId ||
    accessibleClinics.some(clinic => clinic.id === clinicView.clinicId);
  const [viewRestored, setViewRestored] = useState(false);

  useEffect(() => {
    if (!userId) return;
    clearSavedLoginTarget();
    if (!clinicView || !viewReachable) return;
    if (clinicView.clinicId) setActiveClinicId(clinicView.clinicId);
    setAllLocationsActive(clinicView.allLocations);
    setViewRestored(true);
  }, [
    userId,
    clinicView,
    viewReachable,
    setActiveClinicId,
    setAllLocationsActive,
  ]);

  if (isLoading) {
    return <DelayedPageLoader />;
  }

  if (redirectTarget) {
    // The view commits in the same render as this flag, so the page below
    // never renders under the clinic it replaced.
    if (clinicView && viewReachable && !viewRestored) {
      return <DelayedPageLoader />;
    }
    return <Navigate to={viewReachable ? redirectTarget.path : '/'} replace />;
  }

  // Otherwise, render the public route (login, etc.)
  return <Outlet />;
}
